import type { AuditLogResponse, UserResponse } from '@/shared/api';

export type ActivityKind = 'account' | 'change';

export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  at: string;
  title: string;
  /** Der rohe Vorgangsname des Protokolls — bleibt sichtbar, wo es einen gibt. */
  rawAction?: string;
  /** Bereich des betroffenen Datensatzes, bereits in Produktsprache. */
  area?: string;
  detail?: string | null;
  diff?: string | null;
  /** Anzahl zusammengefasster Wiederholungen; 1 bedeutet „einzeln". */
  count: number;
  /** Ältester Zeitpunkt der Zusammenfassung — nur gesetzt, wenn count > 1. */
  since?: string;
}

interface Labels {
  entity: (entityType: string) => string;
  action: (action: string) => string;
}

/**
 * Die Zeitleiste eines Kontos — ausschließlich aus vorhandenen Daten.
 *
 * <p>Zwei Quellen, kein neuer Schreibpfad: die beiden Zeitstempel, die auf dem Benutzer selbst
 * stehen (<c>CreatedAt</c>, <c>LastLoginAt</c>), und das bestehende Änderungsprotokoll, gefiltert
 * auf diesen Auslöser. Es gibt bewusst keine eigene Anmeldehistorie und keine IP-Spalte —
 * PO-Entscheid vom 19.08.2026.</p>
 *
 * <p>Was der Vertrag nicht datiert, wird auch nicht datiert: „Einladung angenommen" ist im
 * Vertrag ein Wahrheitswert ohne Zeitstempel und steht deshalb als Zustand über der Leiste,
 * nicht als erfundener Punkt darin.</p>
 *
 * <p>Aufeinanderfolgende gleiche Vorgänge werden zu einer Zeile mit Anzahl und Zeitspanne
 * gefaltet. Der Augenschein hat gezeigt, warum: eine Stunde Arbeit erzeugt zwei Dutzend
 * „Sitzung verlängert", und der eine Eintrag, um den es geht, steht danach unter dem Falz. Das
 * ist eine Darstellungsentscheidung, keine Auswahl — gezählt wird jeder Eintrag.</p>
 */
export function buildUserActivity(
  user: UserResponse,
  auditEntries: AuditLogResponse[],
  labels: Labels,
): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  if (user.lastLoginAt) {
    entries.push({
      id: 'account-last-login',
      kind: 'account',
      at: user.lastLoginAt,
      title: 'Zuletzt angemeldet',
      count: 1,
    });
  }

  entries.push({
    id: 'account-created',
    kind: 'account',
    at: user.createdAt,
    title: 'Konto angelegt',
    count: 1,
  });

  for (const entry of auditEntries) {
    entries.push({
      id: entry.id,
      kind: 'change',
      at: entry.createdAt,
      title: labels.action(entry.action),
      // Nur zeigen, wenn die Übersetzung greift — sonst stünde derselbe Rohwert zweimal.
      rawAction: labels.action(entry.action) === entry.action ? undefined : entry.action,
      area: labels.entity(entry.entityType),
      // Die Kennung des betroffenen Datensatzes hilft beim Abgleich mit dem Protokoll und ist
      // keine Personendatenanzeige.
      detail: entry.entityId ? `${entry.entityId.slice(0, 8)}…` : null,
      diff: entry.diff,
      count: 1,
    });
  }

  entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return foldRepeats(entries);
}

/** Faltet direkt aufeinanderfolgende Zeilen mit identischem Titel und Bereich. */
function foldRepeats(entries: ActivityEntry[]): ActivityEntry[] {
  const folded: ActivityEntry[] = [];
  for (const entry of entries) {
    const previous = folded[folded.length - 1];
    const sameEvent =
      previous !== undefined &&
      previous.kind === 'change' &&
      entry.kind === 'change' &&
      previous.title === entry.title &&
      previous.area === entry.area;
    if (sameEvent && previous) {
      previous.count += 1;
      previous.since = entry.at;
      // Ein gefalteter Block trägt keinen einzelnen Vorher/Nachher-Block mehr — er gehörte
      // sonst willkürlich zum jüngsten der zusammengefassten Einträge.
      previous.diff = null;
      previous.detail = null;
    } else {
      folded.push({ ...entry });
    }
  }
  return folded;
}
